import {
  QueryClient,
  type QueryKey,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { ApiError, api, newRequestId, type QueryParams } from './client'

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Client errors (4xx) are answers, not glitches: never retry them.
        retry: (count, error) =>
          !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      },
      mutations: { retry: false },
    },
  })
}

export interface ApiQueryOptions<T>
  extends Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'> {
  query?: QueryParams
}

/** `GET` a path under `/api/v1` through TanStack Query. The signal aborts on unmount/key change. */
export function useApiQuery<T>(key: QueryKey, path: string, options: ApiQueryOptions<T> = {}) {
  const { query, ...rest } = options
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<T>(path, { query, signal }),
    ...rest,
  })
}

export interface MutationContext {
  /** The command's `X-Request-Id`. Pass it to `api.post(..., { requestId })`. */
  requestId: string
}

export interface ApiMutationOptions<TData, TVars> {
  onSuccess?: (data: TData, vars: TVars) => unknown
  onError?: (error: ApiError | Error, vars: TVars) => unknown
  /** Query keys to invalidate after success. */
  invalidate?: QueryKey[]
}

/**
 * Single-flight mutation (UXR-015, GAP-UI-028, FORM-023):
 * - `mutate`/`mutateAsync` are no-ops while a call is pending (double submit does nothing)
 * - each attempt gets an `X-Request-Id`; retrying the *same* variables after a failure reuses
 *   it, so the server replays instead of duplicating; new variables or a success get a new id
 */
export function useApiMutation<TVars, TData = unknown>(
  mutationFn: (vars: TVars, context: MutationContext) => Promise<TData>,
  options: ApiMutationOptions<TData, TVars> = {},
) {
  const queryClient = useQueryClient()
  const inFlight = useRef<Promise<TData> | null>(null)
  const attempt = useRef<{ fingerprint: string; requestId: string } | null>(null)
  const { onSuccess, onError, invalidate } = options

  const mutation = useMutation<TData, ApiError | Error, { vars: TVars; requestId: string }>({
    mutationFn: ({ vars, requestId }) => mutationFn(vars, { requestId }),
    onSuccess: async (data, { vars }) => {
      attempt.current = null
      await Promise.all(
        (invalidate ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      )
      await onSuccess?.(data, vars)
    },
    onError: (error, { vars }) => onError?.(error, vars),
  })
  const { mutateAsync: runAsync } = mutation

  const mutateAsync = useCallback(
    (vars: TVars): Promise<TData> => {
      if (inFlight.current) return inFlight.current
      const fingerprint = fingerprintOf(vars)
      if (attempt.current?.fingerprint !== fingerprint) {
        attempt.current = { fingerprint, requestId: newRequestId() }
      }
      const promise = runAsync({ vars, requestId: attempt.current.requestId }).finally(() => {
        inFlight.current = null
      })
      inFlight.current = promise
      return promise
    },
    [runAsync],
  )

  const mutate = useCallback(
    (vars: TVars) => {
      mutateAsync(vars).catch(() => {
        // Surfaced through `error` / `onError`.
      })
    },
    [mutateAsync],
  )

  return { ...mutation, mutate, mutateAsync }
}

function fingerprintOf(value: unknown): string {
  try {
    return JSON.stringify(value) ?? 'undefined'
  } catch {
    return String(Math.random())
  }
}
